import { z } from 'zod';

import {
  PROOF_EXTRACTION_LIMITS,
  proofExtractionAdapterSchema,
  proofCandidateImportanceSchema,
  proofExtractionObjectClassSchema,
  proofIdentifierSchema,
  proofRunStateSchema,
} from './contracts.js';

export const PROOF_SOURCE_BUNDLE_SCHEMA_VERSION = 'proof-source-bundle/v1' as const;
export const PROOF_SOURCE_LIMITS_VERSION = 'proof-limits/v1' as const;
export const PROOF_SOURCE_RETENTION_DAYS = 7;

const FILE_MIME_BY_EXTENSION = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  csv: 'text/csv',
  txt: 'text/plain',
  md: 'text/markdown',
  html: 'text/html',
  htm: 'text/html',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
} as const;

export const PROOF_SOURCE_DETECTED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/csv',
  'text/plain',
  'text/markdown',
  'text/html',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

export const proofSourceDetectedMimeTypeSchema = z.enum(PROOF_SOURCE_DETECTED_MIME_TYPES);

export type ProofSourceFileExtension = keyof typeof FILE_MIME_BY_EXTENSION;

export function expectedProofSourceMimeType(filename: string): string | null {
  const extension = fileExtension(filename);
  return extension ? FILE_MIME_BY_EXTENSION[extension] : null;
}

export const proofFileSourceSchema = z
  .object({
    kind: z.literal('file'),
    clientKey: z.string().min(1).max(120),
    filename: z.string().min(1).max(255),
    declaredMimeType: z.string().min(1).max(160),
    bytes: z.number().int().positive(),
  })
  .strict();

export const proofUrlSourceSchema = z
  .object({
    kind: z.literal('url'),
    clientKey: z.string().min(1).max(120),
    url: z.string().min(1).max(2_048),
  })
  .strict();

export const proofSourceDescriptorSchema = z.discriminatedUnion('kind', [
  proofFileSourceSchema,
  proofUrlSourceSchema,
]);

export const proofSourceBundleRequestSchema = z
  .object({
    schemaVersion: z.literal(PROOF_SOURCE_BUNDLE_SCHEMA_VERSION),
    idempotencyKey: z.string().min(16).max(200),
    sources: z.array(proofSourceDescriptorSchema).min(1).max(PROOF_EXTRACTION_LIMITS.assetsPerBundle),
    locationIds: z.array(proofIdentifierSchema).max(10).default([]),
  })
  .strict();

export type ProofSourceDescriptor = z.infer<typeof proofSourceDescriptorSchema>;
export type ProofSourceBundleRequest = z.infer<typeof proofSourceBundleRequestSchema>;

export interface PreparedProofSourceBundle extends ProofSourceBundleRequest {
  fileCount: number;
  urlCount: number;
  totalKnownBytes: number;
  maxCostMicrodollars: number;
  retentionDays: number;
}

export class ProofSourceBundleValidationError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'ProofSourceBundleValidationError';
  }
}

function normalizeMime(value: string): string {
  return (value.split(';', 1)[0] ?? '').trim().toLowerCase();
}

function fileExtension(filename: string): ProofSourceFileExtension | null {
  const normalized = filename.normalize('NFKC').trim();
  if (
    !normalized ||
    normalized.includes('/') ||
    normalized.includes('\\') ||
    Array.from(normalized).some((character) => character.charCodeAt(0) <= 31 || character.charCodeAt(0) === 127)
  ) {
    return null;
  }
  const extension = normalized.split('.').pop()?.toLowerCase();
  return extension && extension in FILE_MIME_BY_EXTENSION ? (extension as ProofSourceFileExtension) : null;
}

function normalizeUrl(rawUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl.trim());
  } catch {
    throw new ProofSourceBundleValidationError('invalid_source_url', 'Every source URL must be a complete HTTPS URL.');
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password || !parsed.hostname) {
    throw new ProofSourceBundleValidationError('invalid_source_url', 'Every source URL must be a complete HTTPS URL.');
  }
  parsed.hash = '';
  return parsed.toString();
}

export function prepareProofSourceBundle(input: unknown): PreparedProofSourceBundle {
  const parsed = proofSourceBundleRequestSchema.parse(input);
  const seenClientKeys = new Set<string>();
  const seenSources = new Set<string>();
  let totalKnownBytes = 0;
  let fileCount = 0;
  let urlCount = 0;

  const sources = parsed.sources.map((source): ProofSourceDescriptor => {
    if (seenClientKeys.has(source.clientKey)) {
      throw new ProofSourceBundleValidationError('duplicate_client_key', 'Each selected source must have a unique client key.');
    }
    seenClientKeys.add(source.clientKey);
    if (source.kind === 'url') {
      const url = normalizeUrl(source.url);
      const identity = `url:${url}`;
      if (seenSources.has(identity)) {
        throw new ProofSourceBundleValidationError('duplicate_source', 'The same source URL was selected more than once.');
      }
      seenSources.add(identity);
      urlCount += 1;
      return { ...source, url };
    }

    const extension = fileExtension(source.filename);
    if (!extension) {
      throw new ProofSourceBundleValidationError('unsupported_extension', `${source.filename} is not a supported source file.`);
    }
    const declaredMimeType = normalizeMime(source.declaredMimeType);
    if (declaredMimeType !== FILE_MIME_BY_EXTENSION[extension]) {
      throw new ProofSourceBundleValidationError(
        'declared_mime_mismatch',
        `${source.filename} does not match its declared file type.`,
      );
    }
    if (source.bytes > PROOF_EXTRACTION_LIMITS.bytesPerAsset) {
      throw new ProofSourceBundleValidationError('asset_size_limit', `${source.filename} exceeds the 25 MB source limit.`);
    }
    const identity = `file:${source.filename.normalize('NFKC').trim().toLowerCase()}:${source.bytes}:${declaredMimeType}`;
    if (seenSources.has(identity)) {
      throw new ProofSourceBundleValidationError('duplicate_source', `${source.filename} was selected more than once.`);
    }
    seenSources.add(identity);
    totalKnownBytes += source.bytes;
    fileCount += 1;
    return { ...source, filename: source.filename.normalize('NFKC').trim(), declaredMimeType };
  });

  if (totalKnownBytes > PROOF_EXTRACTION_LIMITS.bytesPerBundle) {
    throw new ProofSourceBundleValidationError('bundle_size_limit', 'Selected files exceed the 100 MB bundle limit.');
  }

  return {
    ...parsed,
    // The submitted scope is part of the run fingerprint. Make equivalent choices
    // produce the same fingerprint and avoid duplicate context labels.
    locationIds: [...new Set(parsed.locationIds)].sort(),
    sources,
    fileCount,
    urlCount,
    totalKnownBytes,
    maxCostMicrodollars: PROOF_EXTRACTION_LIMITS.runCostMicrodollars,
    retentionDays: PROOF_SOURCE_RETENTION_DAYS,
  };
}

export const proofRunAssetSummarySchema = z
  .object({
    ref: z.string().regex(/^[a-f0-9]{12}$/),
    state: z.enum(['staged', 'validating', 'ready', 'processing', 'processed', 'rejected', 'partial', 'failed', 'unsupported', 'excluded']),
    adapter: proofExtractionAdapterSchema.nullable(),
    detectedMimeType: proofSourceDetectedMimeTypeSchema.nullable(),
    bytes: z.number().int().nonnegative(),
    expectedObjects: z.number().int().nonnegative(),
    detectedObjects: z.number().int().nonnegative(),
    errorCode: z.string().regex(/^[a-z][a-z0-9_]{0,119}$/).nullable(),
  })
  .strict();

export const proofRunObjectFailureSchema = z
  .object({
    ref: z.string().regex(/^[a-f0-9]{12}$/),
    assetRef: z.string().regex(/^[a-f0-9]{12}$/),
    objectClass: z.union([proofExtractionObjectClassSchema, z.literal('unknown')]),
    state: z.enum(['pending', 'processing', 'partial', 'failed', 'unsupported']),
    errorCode: z.string().regex(/^[a-z][a-z0-9_]{0,119}$/).nullable(),
  })
  .strict();

export const proofRunUploadIssueSchema = z
  .object({
    ref: z.string().regex(/^[a-f0-9]{12}$/),
    state: z.enum(['issued', 'finalizing', 'ready', 'rejected', 'cancelled', 'expired', 'deleted']),
    errorCode: z.string().regex(/^[a-z][a-z0-9_]{0,119}$/).nullable(),
  })
  .strict();

export const proofRunCreatedProofSchema = z
  .object({
    id: z.string().uuid(),
    labelShort: z.string().min(1).max(160),
    status: z.enum(['draft', 'pending_approval', 'approved', 'ready', 'expired', 'revoked', 'archived']),
  })
  .strict();

export const proofSourceRunOutcomeSchema = z
  .object({
    type: z.enum(['review_required', 'proof_created', 'candidate_rejected', 'candidate_duplicate', 'no_change', 'analysis_incomplete']),
    summary: z.string().min(1).max(240),
    occurredAt: z.string().datetime(),
  })
  .strict();

/** Safe identity only. Candidate claims and evidence remain in the governed review endpoint. */
export const proofRunPendingCandidateSchema = z
  .object({
    id: proofIdentifierSchema,
    labelShort: z.string().min(1).max(160),
    locationIds: z.array(proofIdentifierSchema).max(10),
    storeLabel: z.string().min(1).max(160).nullable().optional(),
    importance: proofCandidateImportanceSchema,
    criticalNumeric: z.boolean(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export const proofExtractionRunSummarySchema = z
  .object({
    id: z.string().uuid(),
    runGeneration: z.number().int().positive(),
    state: proofRunStateSchema,
    expectedAssetCount: z.number().int().min(1).max(PROOF_EXTRACTION_LIMITS.assetsPerBundle),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    terminalAt: z.string().datetime().nullable(),
    coveragePassed: z.boolean(),
    usageReconciled: z.boolean(),
    unresolvedContradictions: z.number().int().nonnegative(),
    taskCounts: z.record(z.string(), z.number().int().nonnegative()),
    actualCostMicrodollars: z.number().int().nonnegative(),
    candidateCount: z.number().int().nonnegative(),
    pendingCandidateCount: z.number().int().nonnegative(),
    sourceLocationIds: z.array(proofIdentifierSchema).max(10).optional(),
    sourceStoreLabels: z.array(z.string().min(1).max(160)).max(10).optional(),
    sourceOrigin: z.enum(['manual_upload', 'linked_url', 'scheduled_analytics', 'system']).optional(),
    sourceLabel: z.string().min(1).max(160).optional(),
    recentOutcomes: z.array(proofSourceRunOutcomeSchema).max(5).optional(),
    // Optional for history-response compatibility; new server responses always include it.
    pendingCandidates: z.array(proofRunPendingCandidateSchema).max(100).optional(),
    createdProofCount: z.number().int().nonnegative(),
    createdProofs: z.array(proofRunCreatedProofSchema).max(20),
    failureCodes: z.array(z.string().regex(/^[a-z][a-z0-9_]{0,119}$/)).max(20),
    assets: z.array(proofRunAssetSummarySchema).max(PROOF_EXTRACTION_LIMITS.assetsPerBundle),
    objectFailures: z.array(proofRunObjectFailureSchema).max(PROOF_EXTRACTION_LIMITS.childObjectsPerBundle),
    uploadIssues: z.array(proofRunUploadIssueSchema).max(PROOF_EXTRACTION_LIMITS.childObjectsPerBundle),
    canQueue: z.boolean(),
    canResume: z.boolean(),
    canRetry: z.boolean(),
    canExclude: z.boolean(),
    canCancel: z.boolean(),
    draftEligible: z.boolean(),
  })
  .strict();

export type ProofExtractionRunSummary = z.infer<typeof proofExtractionRunSummarySchema>;

export function formatProofSourceBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${bytes} B`;
}
